/**
 * 指挥端：录制语音 → command-sign → OSS POST → command-commit → 返回 media_id
 * 仅使用后端 MIME_TABLE 支持的音频类型（ogg / mp4 / mpeg / aac / amr）。
 */
import apiFetch from '../apiFetch';

const PREFERRED_MIME = [
  'audio/ogg;codecs=opus',
  'audio/ogg',
  'audio/mp4',
  'audio/aac',
  'audio/mpeg'
];

export function pickRecorderMime() {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return '';
  }
  for (const mime of PREFERRED_MIME) {
    try {
      if (MediaRecorder.isTypeSupported(mime)) return mime;
    } catch (_) { /* ignore */ }
  }
  return '';
}

function normalizeMime(mime) {
  const raw = String(mime || '').split(';')[0].trim().toLowerCase();
  if (raw === 'audio/m4a' || raw === 'audio/x-m4a') return 'audio/mp4';
  if (raw === 'audio/mp3') return 'audio/mpeg';
  return raw;
}

function uuidLite() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cmd_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

async function postOss(upload, blob) {
  if (!upload || upload.mode !== 'post' || !upload.url || !upload.fields) {
    throw new Error('上传凭证无效');
  }
  const form = new FormData();
  Object.entries(upload.fields).forEach(([k, v]) => {
    form.append(k, v);
  });
  const fileField = upload.file_field || 'file';
  form.append(fileField, blob, `voice.${(blob.type || 'audio/ogg').includes('mp4') ? 'm4a' : 'ogg'}`);
  const res = await fetch(upload.url, { method: 'POST', body: form });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`OSS 上传失败（HTTP ${res.status}）${text ? `: ${text.slice(0, 120)}` : ''}`);
  }
}

/**
 * @param {Blob} blob
 * @param {{ durationMs: number, mime?: string }} opts
 * @returns {Promise<{ media_id: string, mime: string, duration_ms: number }>}
 */
export async function uploadCommandVoiceBlob(blob, opts = {}) {
  if (!blob || !(blob instanceof Blob) || blob.size <= 0) {
    throw new Error('录音为空');
  }
  const durationMs = Math.max(200, Math.round(Number(opts.durationMs) || 0));
  if (!Number.isFinite(durationMs) || durationMs <= 0) {
    throw new Error('缺少录音时长');
  }
  const mime = normalizeMime(opts.mime || blob.type || 'audio/ogg');
  if (!mime.startsWith('audio/')) {
    throw new Error(`不支持的音频类型：${mime || 'unknown'}`);
  }

  const signRes = await apiFetch('/api/tracker/media/command-sign', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      mime,
      kind: 'audio',
      size: blob.size,
      duration_ms: durationMs,
      client_uuid: uuidLite()
    })
  });
  const signRaw = await signRes.text();
  let signData = {};
  try { signData = signRaw ? JSON.parse(signRaw) : {}; } catch (_) { /* ignore */ }
  if (!signRes.ok || signData.success === false) {
    throw new Error(signData.error || signData.message || `申请上传失败（HTTP ${signRes.status}）`);
  }
  const mediaId = signData.media_id;
  if (!mediaId) throw new Error('未返回 media_id');

  if (signData.already_committed) {
    return { media_id: mediaId, mime, duration_ms: durationMs };
  }
  if (!signData.upload) {
    throw new Error('未返回上传凭证');
  }
  await postOss(signData.upload, blob);

  const commitRes = await apiFetch('/api/tracker/media/command-commit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ media_id: mediaId })
  });
  const commitRaw = await commitRes.text();
  let commitData = {};
  try { commitData = commitRaw ? JSON.parse(commitRaw) : {}; } catch (_) { /* ignore */ }
  if (!commitRes.ok || commitData.success === false) {
    throw new Error(commitData.error || commitData.message || `确认上传失败（HTTP ${commitRes.status}）`);
  }
  return { media_id: mediaId, mime, duration_ms: durationMs };
}

/**
 * 对指定队员下发语音指令（需已有绑定令牌）。
 */
export async function sendCommandVoiceToMember(member, blob, opts = {}) {
  const uploaded = await uploadCommandVoiceBlob(blob, opts);
  const body = {
    media_ids: [uploaded.media_id],
    command_kind: 'voice',
    important: false,
    pin_on_map: false,
    device_unique_ids: member?.device_unique_id ? [member.device_unique_id] : [],
    names: member?.name ? [member.name] : []
  };
  const response = await apiFetch('/api/tracker/commands', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const raw = await response.text();
  let data = {};
  try { data = raw ? JSON.parse(raw) : {}; } catch (_) { /* ignore */ }
  if (!response.ok || !data.success) {
    throw new Error(data.error || `下达语音失败（HTTP ${response.status}）`);
  }
  return { ...data, media_id: uploaded.media_id, duration_ms: uploaded.duration_ms, mime: uploaded.mime };
}
